import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listUserSecurityPostureDataTable,
  getUserSecurityPostureFilterOptions,
} from '@kirin/api-client/api/user-security-posture';
import type {
  UserSecurityPostureDataTableItem,
  UserSecurityPostureDataTableSortField,
  UserSecurityPostureFilterOptionField,
} from '@kirin/api-client/contracts/user-security-posture';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Fetch user security posture data for the DataTable component.
 */
export async function fetchUserSecurityPostureDataTable(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<UserSecurityPostureDataTableItem>> {
  const response = await listUserSecurityPostureDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as
        | UserSecurityPostureDataTableSortField
        | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
    },
  };
}

/**
 * Fetch distinct filter option values for a user security posture column.
 * Returns a paginated page shape so the DataTable's useInfiniteQuery can
 * determine whether more options exist and what offset to use next.
 */
export async function fetchUserSecurityPostureFilterOptions(
  organizationId: string,
  field: UserSecurityPostureFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getUserSecurityPostureFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    }
  );
  const offset = params.offset ?? 0;
  const hasMore =
    response.options.length > 0 &&
    offset + response.options.length < response.total;
  return {
    options: response.options,
    total: response.total,
    hasMore,
    nextOffset: hasMore ? offset + response.options.length : null,
  };
}

/**
 * Query key for user security posture DataTable data.
 */
export function getUserSecurityPostureDataTableQueryKey(
  organizationId: string,
  params: DataFetchParams
): readonly unknown[] {
  return queryKeys.userSecurityPosture.dataTable(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
}

/**
 * Query key for user security posture filter options.
 */
export function getUserSecurityPostureFilterOptionsQueryKey(
  organizationId: string,
  field: UserSecurityPostureFilterOptionField,
  params: { search?: string; limit?: number }
): readonly unknown[] {
  return queryKeys.userSecurityPosture.filterOptions(
    organizationId,
    field,
    params
  );
}
